import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/joy';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import { trackEvent } from '@mono-farmart-web/analytics/utils/moengage';
import Events from '@mono-farmart-web/analytics/constant/MoengageEvents/index';
import useAINavigation from '../../../hooks/navigation';
import { useNavigation } from '@mono-farmart-web/navigation-context/context/Navigation';
import useI18n from '@mono-farmart-web/i18n/hooks/usei18n';
import {
	SAFE_AREA_TOP,
	withSafeTop,
} from '@mono-farmart-web/ui-components/component/UnifiedHeader/safeArea';
import chat_empty from '../../../assets/chat_empty_img.png';
import HeaderTooltip from './tooltip';
import { useChatUiStore } from '../../../stores/chat-ui.store';

interface HeaderProps {
	title?: string;
	showBackButton?: boolean;
	showMenuButton?: boolean;
	previousPathname?: string;
}

const Header: React.FC<HeaderProps> = ({
	title,
	showBackButton = true,
	showMenuButton = true,
	previousPathname,
}) => {
	const { t } = useI18n();
	const navigate = useNavigate();
	const { navigateTo } = useAINavigation();
	const hasMessages = useChatUiStore(state => state.hasMessages);

	const [tooltipStep, setTooltipStep] = useState<number>(0); // 0: none, 1: step1, 2: step2, 3: complete

	useEffect(() => {
		if (!hasMessages) {
			setTooltipStep(0);
			return;
		}
		const tooltipsSeen = localStorage?.getItem('ai.headerTooltipsSeen');
		if (!tooltipsSeen) {
			setTooltipStep(1);
		}
	}, [hasMessages]);

	const handleTooltipNext = () => {
		setTooltipStep(2);
	};

	const handleTooltipDone = () => {
		localStorage?.setItem('ai.headerTooltipsSeen', 'true');
		setTooltipStep(3);
	};

	const handleBack = useCallback(() => {
		if (previousPathname) {
			navigate(previousPathname);
		} else {
			navigate('/');
		}
		trackEvent(Events.onAiTopNavClicked, {
			cta: 'back',
		});
	}, [navigate, previousPathname]);

	function handleNewChat() {
		navigateTo(`/`);
		trackEvent(Events.onAiTopNavClicked, {
			cta: 'newChat',
		});
	}

	function handleHistoryClick() {
		navigateTo(`/history`);
		trackEvent(Events.onAiTopNavClicked, {
			cta: 'history',
		});
	}

	const { updateNavigationConfig, navigationConfig } = useNavigation();

	useEffect(() => {
		return updateNavigationConfig({
			onBackButtonClick: handleBack,
			showBackButton: true,
		});
	}, [handleBack, showBackButton, updateNavigationConfig]);

	return (
		<Box
			component='header'
			sx={{
				position: 'sticky',
				top: 0,
				zIndex: 1000,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				height: 'auto',
				padding: '8px 16px',
				paddingTop: withSafeTop('8px'),
				minHeight: `calc(56px + ${SAFE_AREA_TOP})`,
				boxSizing: 'border-box',
			}}
		>
			{/* Left: Back Button */}
			{navigationConfig.showBackButton && (
				<IconButton
					variant='plain'
					onClick={navigationConfig.onBackButtonClick}
					sx={{ paddingLeft: 0 }}
				>
					<ArrowBackIcon sx={{ color: 'text.primary' }} />
				</IconButton>
			)}

			{/* Center: AI Icon and Title */}
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					alignItems: hasMessages ? 'flex-start' : 'center',
					justifyContent: 'center',
					gap: '4px',
				}}
			>
				{!hasMessages && (
					<>
						<Box
							component='img'
							src={chat_empty}
							alt='FarMart AI'
							sx={{
								width: '32px',
								height: '32px',
								objectFit: 'contain',
							}}
						/>
						{title && (
							<Typography level='body-xxs' sx={{ textAlign: 'center' }}>
								{title}
							</Typography>
						)}
					</>
				)}
				{hasMessages && <Typography level='title-sm'>{title}</Typography>}
			</Box>

			{hasMessages && (
				<HeaderTooltip
					open={tooltipStep === 1}
					onClose={handleTooltipNext}
					onNext={handleTooltipNext}
					description='Have a new chat here'
					actionText='NEXT '
				>
					<IconButton
						variant='plain'
						onClick={handleNewChat}
						sx={{
							borderRadius: '8px',
							border: '1px solid',
							borderColor: 'neutral.100',
							backgroundColor: 'neutral.50',
							padding: '6px 12px',
							marginRight: '10px',
						}}
					>
						<AddCommentOutlinedIcon
							sx={{ color: 'text.primary', fontSize: '20px' }}
						/>
					</IconButton>
				</HeaderTooltip>
			)}

			{showMenuButton ? (
				<HeaderTooltip
					open={tooltipStep === 2}
					onClose={handleTooltipDone}
					description='Check your chat history here'
					actionText='DONE '
				>
					<IconButton
						variant='plain'
						onClick={handleHistoryClick}
						sx={{
							borderRadius: '8px',
							border: '1px solid',
							borderColor: 'neutral.100',
							backgroundColor: 'neutral.50',
							padding: '6px 12px',
						}}
					>
						<FormatListBulletedRoundedIcon
							sx={{ color: 'text.primary', fontSize: '20px' }}
						/>
					</IconButton>
				</HeaderTooltip>
			) : (
				<Box sx={{ width: 40 }} />
			)}
		</Box>
	);
};

export default Header;
